import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Channel, Program, RootState, SvodChannel } from '../../interfaces';
import { details } from '../../routes/routes';
import sortChannels from '../../utils/sortChannels';
import { hideChannelList as hideChannelListAction } from '../../views/tv/actions';
import ChannelList from './channelList';
import MiniEpg from './miniEpg';
import './style.scss';
import SvodService from '../../components/PlayerEpg/svodService';
import { Provider } from '../../queries/ats/me/providers/types';
import { useProviders } from '../../queries/ats/me/providers/hooks';

function PlayerEpg({
  channels,
  currentChannel,
  currentProgram,
}: {
  channels: Channel[];
  currentChannel: Channel;
  currentProgram: Program;
}) {
  const auth = useSelector((state: RootState) => state.authReducer);
  const channelListVisible = useSelector((state: RootState) => state.channelsReducer.channelListVisible);

  const svodKiosks = useSelector((state: RootState) => state.app.svodKiosks);

  const { data: providers } = useProviders();

  const sortedChannels = useMemo(() => {
    return sortChannels(channels, auth)
      .reverse()
      .map((channel) => ({
        ...channel,
        svodKiosk: svodKiosks.find((kiosk) => kiosk.associatedChannelIds?.includes(String(channel.contentId))),
      })) as SvodChannel[];
  }, [auth, channels, svodKiosks]);

  const dispatch = useDispatch();

  const [shouldShowEpg, setShouldShowEpg] = useState(true);
  const [focusedChannel, setFocusedChannel] = useState(currentChannel as Channel);
  const [panicking, setPanicking] = useState(false);

  const hideEpg = () => setShouldShowEpg(false);
  const showEpg = () => setShouldShowEpg(true);

  const panic = (() => {
    let timeoutId = 0;

    return () => {
      details.close();
      dispatch(hideChannelListAction());
      setPanicking(true);

      window.clearTimeout(timeoutId);
      timeoutId = window.setTimeout(() => {
        setPanicking(false);
      }, 250);
    };
  })();

  useEffect(
    () => {
      window.addEventListener('resize', panic);
      return () => window.removeEventListener('resize', panic);
    },
    /* eslint-disable-next-line */
    [],
  );

  useEffect(() => {
    if (!channelListVisible && currentChannel) {
      setFocusedChannel(currentChannel);
    }
  }, [channelListVisible, currentChannel]);

  if (!focusedChannel) {
    return null;
  }

  const focusedChannelProvider = providers?.find((provider: Provider) => {
    if (provider.type === 'svod') {
      return provider.params.channelIds?.split(',').includes(focusedChannel.contentId!);
    }
    return undefined;
  });

  return (
    <div className="playerEpg">
      {!panicking && (
        <ChannelList
          channels={sortedChannels}
          focusedChannel={focusedChannel}
          setFocusedChannel={setFocusedChannel}
          showEpg={showEpg}
          hideEpg={hideEpg}
          shouldShowEpg={shouldShowEpg}
          visible={channelListVisible}
          panic={panic}
        />
      )}
      {channelListVisible && shouldShowEpg && (
        <>
          <MiniEpg channel={focusedChannel} />
          {focusedChannelProvider?.type === 'svod' ? <SvodService provider={focusedChannelProvider} /> : null}
        </>
      )}
    </div>
  );
}

export default PlayerEpg;
